import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { JSXElementConstructor, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  SPACE_DATE_RANGE,
  type SpaceDateRange,
} from "@/react/types/AiSummary/SpaceSummary";
import { getMessageHashFromUrl } from "@circle-react/components/Chat/ChatWindowV2/helpers/helpers";
import { ChatRoomMessageSearch } from "@circle-react/components/Chat/MessagingV2/ChatRoomMessageSearch";
import { ChatThreads } from "@circle-react/components/Chat/MessagingV2/ChatThreads";
import { RailBar as MessagingRailbar } from "@circle-react/components/Chat/MessagingV2/RailBar";
import { ChatSpaceSummary } from "@circle-react/components/Spaces/ChatSpace/ChatSpaceSummary";
import { RailBar as SpaceRailbar } from "@circle-react/components/Spaces/ChatSpace/RailBar";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { useSmOrMdOrLgScreenMediaQuery } from "../useMediaQuery";
import { useBoolean } from "../utils/useBoolean";

const RAILBAR_TYPES = {
  PARTICIPANTS: "PARTICIPANTS",
  CHAT_THREADS: "CHAT_THREADS",
  CHAT_ROOM_MESSAGE_SEARCH: "CHAT_ROOM_MESSAGE_SEARCH",
  CHAT_SPACE_SUMMARY: "CHAT_SPACE_SUMMARY",
} as const;

export type RailbarType = keyof typeof RAILBAR_TYPES;

export interface RailbarContextValue {
  chatRoom: any;
  closeSpaceSummaryRailbar: () => void;
  closeThreadsRailbar: () => void;
  dateRange: SpaceDateRange;
  EmbeddedRailbarComponent: JSXElementConstructor<any>;
  firstUnreadMessageId?: string;
  isChatSpace: boolean;
  isCourseSpace: boolean;
  isDmsPage: boolean;
  isDrawer: boolean;
  isLiveStream: boolean;
  isMessageSearchRailbarOpen: boolean;
  messageHashId: string | undefined;
  messageHighlightId: any;
  openSpaceSummaryRailbar: (
    range: SpaceDateRange,
    firstUnreadMessageId?: string,
  ) => void;
  openThreadsRailbar: (message: any, chatRoom: any) => void;
  parentMessage: any;
  RailbarComponent: JSXElementConstructor<any>;
  setDateRange: (range: SpaceDateRange) => void;
  setMessageHighlightId: (messageId: any) => void;
  shouldShowEmbeddedRailBar: boolean;
  shouldShowRailBar: boolean;
  toggleChatRoomMessageSearchRailbar: () => void;
  toggleShowEmbeddedRailBar: () => void;
  toggleShowRailBar: () => void;
  type: RailbarType;
  updateParentMessage: (message: any) => void;
}

const initialState = {
  chatRoom: null,
  closeThreadsRailbar: () => {},
  isCourseSpace: false,
  isLiveStream: false,
  isDrawer: false,
  messageHighlightId: null,
  openThreadsRailbar: () => {},
  parentMessage: null,
  RailbarComponent: () => null,
  setMessageHighlightId: () => {},
  shouldShowRailBar: false,
  toggleShowRailBar: () => {},
  type: RAILBAR_TYPES.PARTICIPANTS,
  updateParentMessage: () => {},
  isChatSpace: false,
  isDmsPage: false,
  toggleChatRoomMessageSearchRailbar: () => {},
  isMessageSearchRailbarOpen: false,
  EmbeddedRailbarComponent: () => null,
  shouldShowEmbeddedRailBar: false,
  toggleShowEmbeddedRailBar: () => {},
  messageHashId: undefined,
  dateRange: SPACE_DATE_RANGE.LAST_7_DAYS,
  setDateRange: () => {},
  openSpaceSummaryRailbar: () => {},
  closeSpaceSummaryRailbar: () => {},
  firstUnreadMessageId: undefined,
};

const RailbarContext = createContext<RailbarContextValue>(initialState);
RailbarContext.displayName = "RailbarContext";

export interface RailbarContextProviderProps {
  children: ReactNode;
  isChatSpace?: boolean;
  isCourseSpace?: boolean;
  isDrawer?: boolean;
  isLiveStream?: boolean;
}

export const RailbarContextProvider = ({
  isChatSpace = false,
  children,
  isLiveStream = false,
  isCourseSpace = false,
  isDrawer = false,
}: RailbarContextProviderProps) => {
  const messageHashId = getMessageHashFromUrl();
  const { message_id: messageThreadId } = useRouterQueryParams();
  const [type, setType] = useState<RailbarType>(initialState.type);
  const [shouldShowRailBar, toggleShowRailBar, setShowRailBar] =
    useBoolean(false);
  const [
    shouldShowEmbeddedRailBar,
    toggleShowEmbeddedRailBar,
    setShowEmbeddedRailBar,
  ] = useBoolean(false);

  const [parentMessage, setParentMessage] = useState<any>(
    initialState.parentMessage,
  );
  const [chatRoom, setChatRoom] = useState<any>(initialState.chatRoom);
  const [messageHighlightId, setMessageHighlightId] =
    useState<any>(messageThreadId);

  const location = useLocation();
  const isDmsPage = location.pathname.startsWith("/messages");

  const isSmallOrMediumDevice = useSmOrMdOrLgScreenMediaQuery();
  const isMessageSearchRailbarOpen =
    type === RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH;

  const [dateRange, setDateRange] = useState<SpaceDateRange>(
    SPACE_DATE_RANGE.LAST_7_DAYS,
  );

  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<
    string | undefined
  >();

  useEffect(() => {
    setShowRailBar(!isSmallOrMediumDevice);
  }, [isSmallOrMediumDevice]);

  const openThreadsRailbar = useCallback(
    (message: any, chatRoom: any) => {
      setParentMessage(message);
      setChatRoom(chatRoom);
      if (isMessageSearchRailbarOpen && messageThreadId) {
        setShowEmbeddedRailBar(true);
      } else {
        setShowEmbeddedRailBar(false);
        setType(RAILBAR_TYPES.CHAT_THREADS);
        setShowRailBar(true);
      }
    },
    [isMessageSearchRailbarOpen, messageThreadId],
  );

  const closeThreadsRailbar = useCallback(() => {
    setParentMessage(null);
    setChatRoom(null);
    setType(RAILBAR_TYPES.PARTICIPANTS);
    isSmallOrMediumDevice && setShowRailBar(false);
  }, [isSmallOrMediumDevice]);

  const toggleChatRoomMessageSearchRailbar = useCallback(() => {
    setType(
      type === RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH
        ? RAILBAR_TYPES.PARTICIPANTS
        : RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH,
    );
    setShowRailBar(true);
  }, [type]);

  const updateParentMessage = (message: any) => {
    setParentMessage(message);
  };

  const getParticipantsRailbarType = useCallback(() => {
    if (isChatSpace) return SpaceRailbar;
    return MessagingRailbar;
  }, [isChatSpace]);

  const openSpaceSummaryRailbar = useCallback(
    (range: SpaceDateRange, messageId?: string) => {
      setDateRange(range);
      setFirstUnreadMessageId(messageId);
      setType(RAILBAR_TYPES.CHAT_SPACE_SUMMARY);
      setShowRailBar(true);
    },
    [],
  );

  const closeSpaceSummaryRailbar = useCallback(() => {
    setType(RAILBAR_TYPES.PARTICIPANTS);
    isSmallOrMediumDevice && setShowRailBar(false);
  }, [isSmallOrMediumDevice]);

  const RAILBAR_COMPONENTS: Record<RailbarType, JSXElementConstructor<any>> = {
    [RAILBAR_TYPES.PARTICIPANTS]: getParticipantsRailbarType(),
    [RAILBAR_TYPES.CHAT_THREADS]: ChatThreads,
    [RAILBAR_TYPES.CHAT_ROOM_MESSAGE_SEARCH]: ChatRoomMessageSearch,
    [RAILBAR_TYPES.CHAT_SPACE_SUMMARY]: ChatSpaceSummary,
  };

  const RailbarComponent = RAILBAR_COMPONENTS[type];
  const EmbeddedRailbarComponent =
    RAILBAR_COMPONENTS[RAILBAR_TYPES.CHAT_THREADS];

  const value: RailbarContextValue = {
    chatRoom,
    closeThreadsRailbar,
    isCourseSpace,
    isLiveStream,
    isChatSpace,
    isDrawer,
    messageHighlightId,
    openThreadsRailbar,
    parentMessage,
    RailbarComponent,
    EmbeddedRailbarComponent,
    setMessageHighlightId,
    shouldShowRailBar,
    toggleShowRailBar,
    type,
    updateParentMessage,
    isDmsPage,
    toggleChatRoomMessageSearchRailbar,
    isMessageSearchRailbarOpen,
    shouldShowEmbeddedRailBar:
      shouldShowEmbeddedRailBar && isMessageSearchRailbarOpen,
    toggleShowEmbeddedRailBar,
    messageHashId,
    dateRange,
    setDateRange,
    openSpaceSummaryRailbar,
    closeSpaceSummaryRailbar,
    firstUnreadMessageId,
  };

  return (
    <RailbarContext.Provider value={value}>{children}</RailbarContext.Provider>
  );
};

export const useRailbar = () => useContext(RailbarContext);
